import { Link } from 'react-router-dom';
import { LegalLayout, LegalSection, useIsJa } from '@/components/layout/LegalLayout';

interface Subprocessor {
  name: string;
  /** Purpose, English then Japanese. */
  purpose: [string, string];
  /** Region, English then Japanese. */
  region: [string, string];
}

const SUBPROCESSORS: Subprocessor[] = [
  {
    name: 'Hetzner Online GmbH',
    purpose: ['Cloud hosting & compute', 'クラウドホスティング・コンピューティング'],
    region: ['Germany (EU)', 'ドイツ（EU）'],
  },
  {
    name: 'Cloudflare, Inc. (R2)',
    purpose: ['Object storage (uploads, exports, backups)', 'オブジェクトストレージ（アップロード・エクスポート・バックアップ）'],
    region: ['EU / global edge', 'EU／グローバルエッジ'],
  },
  {
    name: 'Stripe, Inc.',
    purpose: ['Payments & subscription billing', '決済・サブスクリプション課金'],
    region: ['US / EU', '米国／EU'],
  },
  {
    name: 'Amazon Web Services (SES)',
    purpose: ['Transactional email', 'トランザクションメール'],
    region: ['EU', 'EU'],
  },
  {
    name: 'OpenRouter, Inc.',
    purpose: ['AI model gateway (CadencEZ AI)', 'AIモデルゲートウェイ（CadencEZ AI）'],
    region: ['US', '米国'],
  },
  {
    name: 'Google LLC',
    purpose: ['Optional Google sign-in (SSO)', 'Google サインイン（任意・SSO）'],
    region: ['US / global', '米国／グローバル'],
  },
];

export function SecurityPage() {
  const isJa = useIsJa();

  return (
    <LegalLayout
      title={isJa ? 'セキュリティと委託先' : 'Security & subprocessors'}
      updated="2026-06-03"
      metaTitle={isJa ? 'セキュリティと委託先 — CadencEZ' : 'Security & Subprocessors — CadencEZ'}
      metaDescription={
        isJa
          ? 'CadencEZ のセキュリティ対策（暗号化、アクセス制御、テナント分離、監査ログ）、脆弱性管理、インシデント対応、現在の委託先一覧、およびコンプライアンス状況。'
          : "CadencEZ's security controls — encryption, access control, tenant isolation, audit logging — vulnerability management, incident response, its current subprocessor list, and compliance status."
      }
      path="/security"
    >
      <p>
        {isJa ? (
          <>
            当社はお客様の財務データのセキュリティを重視しています。本ページでは、当社が講じている技術的および組織的措置、委託先、ならびに現在のコンプライアンス状況を要約します。
            セキュリティに関するお問い合わせや脆弱性の報告は{' '}
            <a href="mailto:security@cadencez.com">security@cadencez.com</a> までご連絡ください。
            運営会社は CadencEZ Inc.（米国デラウェア州）です。
          </>
        ) : (
          <>
            We take the security of your financial data seriously. This page summarizes the
            technical and organizational measures we use, our subprocessors, and our current
            compliance status. For security questions or to report a vulnerability, email{' '}
            <a href="mailto:security@cadencez.com">security@cadencez.com</a>. The Service is
            operated by CadencEZ Inc. (Delaware, USA).
          </>
        )}
      </p>

      <LegalSection heading={isJa ? '暗号化' : 'Encryption'}>
        <p>
          {isJa
            ? 'サービスへのすべての通信は TLS により転送時に暗号化されます。機密性の高いシークレット（SSO 証明書、OAuth トークン、コネクタ認証情報）は AES-256-GCM により保存時に暗号化されます。バックアップは暗号化して保管されます。'
            : 'All traffic to the Service is encrypted in transit with TLS. Sensitive secrets — SSO certificates, OAuth tokens, and connector credentials — are encrypted at rest with AES-256-GCM. Backups are stored encrypted.'}
        </p>
      </LegalSection>

      <LegalSection heading={isJa ? 'アクセス制御と分離' : 'Access control & isolation'}>
        <p>
          {isJa
            ? '各お客様のデータはワークスペース単位で論理的に分離され、アプリケーション層の認可がリクエストごとにワークスペースのメンバーシップを検証します。本番システムへのアクセスは最小権限の原則に従い、権限を付与された担当者に限定されます。'
            : "Every customer's data is logically isolated and scoped by workspace; application-level authorization checks workspace membership on every request. Access to production systems follows least-privilege principles and is limited to authorized personnel."}
        </p>
      </LegalSection>

      <LegalSection heading={isJa ? '認証' : 'Authentication'}>
        <p>
          {isJa
            ? 'メールアドレスとパスワード（メール確認付き）、Google サインイン、SAML 2.0 および OIDC によるエンタープライズ SSO に対応し、さらに SCIM 2.0 によるユーザーの自動プロビジョニングおよびデプロビジョニングをサポートします。'
            : 'We support email/password with verification, Google sign-in, and enterprise SSO via SAML 2.0 and OIDC, plus automated user provisioning and deprovisioning through SCIM 2.0.'}
        </p>
      </LegalSection>

      <LegalSection heading={isJa ? '監査ログ' : 'Audit logging'}>
        <p>
          {isJa
            ? '管理操作およびセキュリティ上重要なイベント（サインイン、権限・課金の変更、データの変更）は、実行者・タイムスタンプ・送信元 IP を含む改ざん不可能（イミュータブル）な監査ログに記録され、ワークスペース管理者が参照できます。'
            : 'Administrative and security-relevant events — sign-ins, permission and billing changes, data modifications — are recorded in an immutable audit log with actor, timestamp, and source IP, available to workspace administrators.'}
        </p>
      </LegalSection>

      <LegalSection heading={isJa ? 'データ所在地と耐障害性' : 'Data location & resilience'}>
        <p>
          {isJa ? (
            <>
              お客様データは欧州連合（ドイツ）でホストされます。当社は定期的に暗号化されたデータベースバックアップを取得し、復元テストを実施します。データ移転条件については{' '}
              <Link to="/dpa">DPA</Link> をご参照ください。
            </>
          ) : (
            <>
              Customer data is hosted in the European Union (Germany). We take regular encrypted
              database backups and test restoration. See our data-transfer terms in the{' '}
              <Link to="/dpa">DPA</Link>.
            </>
          )}
        </p>
      </LegalSection>

      <LegalSection heading={isJa ? '脆弱性管理と責任ある開示' : 'Vulnerability management & responsible disclosure'}>
        <p>
          {isJa ? (
            <>
              脆弱性の疑いがある事象は{' '}
              <a href="mailto:security@cadencez.com">security@cadencez.com</a>{' '}
              までご報告ください。当社は報告を確認のうえ調査します。スコープの範囲内で誠実に行動する研究者（プライバシーの侵害、サービスの劣化、データの持ち出しを伴わないもの）に対しては、法的措置等を講じません（セーフハーバー）。公開による開示の前に、修正のための合理的な期間をご提供くださいますようお願いいたします。
            </>
          ) : (
            <>
              Report suspected issues to{' '}
              <a href="mailto:security@cadencez.com">security@cadencez.com</a>. We acknowledge
              and investigate reports. Good-faith researchers acting within scope — no privacy
              violations, no service degradation, and no data exfiltration — will not be pursued
              (safe harbor). Please allow reasonable time to remediate before public disclosure.
            </>
          )}
        </p>
      </LegalSection>

      <LegalSection heading={isJa ? 'インシデント対応と侵害通知' : 'Incident response & breach notification'}>
        <p>
          {isJa
            ? '当社は文書化されたインシデント対応プロセスを備えています。すなわち、検知・トリアージ・封じ込めを行い、影響を受けるお客様に不当な遅滞なく通知し、お客様自身の通知義務の履行を支援するための情報を提供し、インシデント後のレビューを実施します。'
            : "We maintain a documented process: detect, triage, and contain; notify affected customers without undue delay; provide information to support customers' own notification duties; and conduct a post-incident review."}
        </p>
      </LegalSection>

      <LegalSection heading={isJa ? 'セキュアな開発とテスト' : 'Secure development & testing'}>
        <p>
          {isJa
            ? '当社はコードレビュー、依存関係および脆弱性のスキャン、ローテーションを伴うシークレット管理を実施しています。加えて、定期的に社内および第三者によるセキュリティテストを行っています。'
            : 'We use code review, dependency and vulnerability scanning, and secrets management with rotation. We also conduct periodic internal and third-party security testing.'}
        </p>
      </LegalSection>

      <LegalSection heading={isJa ? '人的セキュリティ' : 'Personnel security'}>
        <p>
          {isJa
            ? '担当者は秘密保持契約に拘束され、アクセスは最小権限に基づいて付与されます。当社はセキュリティ意識向上の取り組みを行い、退職・離任時にはアクセス権を速やかに剥奪します。'
            : 'Personnel are bound by confidentiality agreements, and access is granted on a least-privilege basis. We maintain security-awareness practices and promptly revoke access on offboarding.'}
        </p>
      </LegalSection>

      <LegalSection heading={isJa ? '事業継続とバックアップ' : 'Business continuity & backups'}>
        <p>
          {isJa
            ? '当社は定期的に暗号化されたバックアップを取得し、定期的に復元テストを実施しています。'
            : 'We take regular encrypted backups and periodically test restoration from them.'}
        </p>
      </LegalSection>

      <LegalSection heading={isJa ? 'データ所在地の選択肢' : 'Data residency options'}>
        <p>
          {isJa ? (
            <>
              既定では EU／ドイツにデータを保管します。Enterprise プランでは日本国内にデータを保管するオプションをご利用いただけます。移転条件については{' '}
              <Link to="/dpa">DPA</Link> をご参照ください。
            </>
          ) : (
            <>
              By default, data resides in the EU / Germany. A Japan-resident data option is
              available on Enterprise. See the <Link to="/dpa">DPA</Link> for transfer terms.
            </>
          )}
        </p>
      </LegalSection>

      <LegalSection heading={isJa ? '委託先の審査' : 'Vendor / sub-processor vetting'}>
        <p>
          {isJa
            ? '各委託先は、当社と同等以上の保護を定めるデータ処理契約に拘束され、導入前に審査を受けます。新たな委託先を追加する場合は、追加前に変更内容を本ページに掲載します。'
            : 'Each sub-processor is bound by a data-processing agreement with protections no less protective than our own and is reviewed before onboarding. We post changes here before adding new ones.'}
        </p>
      </LegalSection>

      <LegalSection heading={isJa ? 'コンプライアンス状況' : 'Compliance status'}>
        <p>
          {isJa ? (
            <>
              当社の管理策は SOC 2 トラストサービス基準に基づいて設計されています。正式な SOC 2 Type II の認証（アテステーション）は当社のロードマップ上にあり、まだ取得していません。取得後は本ページに掲載します。最新のコンプライアンス状況の確認やお客様の要件のご相談は{' '}
              <a href="mailto:security@cadencez.com">security@cadencez.com</a> までご連絡ください。
            </>
          ) : (
            <>
              Our controls are designed around the SOC 2 Trust Services Criteria. A formal SOC 2
              Type II attestation is on our roadmap and not yet issued; we will publish it here
              when complete. For the latest compliance status or to discuss your requirements,
              contact <a href="mailto:security@cadencez.com">security@cadencez.com</a>.
            </>
          )}
        </p>
      </LegalSection>

      <LegalSection heading={isJa ? '委託先一覧' : 'Subprocessors'}>
        <p>
          {isJa
            ? 'サービスの運営にあたり、当社は以下の委託先を利用しています。当社は各委託先に対し、当社と同等以上の保護を定めるデータ保護義務を課しており、新たな委託先を追加する場合は追加前に本ページに掲載します。'
            : 'We engage the following subprocessors to operate the Service. We require each to meet data-protection obligations no less protective than our own and will post updates here before adding new ones.'}
        </p>
        <div className="mt-3 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-ink-500 border-b border-ink-200">
                <th className="py-2 pr-4 font-medium">{isJa ? '委託先' : 'Subprocessor'}</th>
                <th className="py-2 pr-4 font-medium">{isJa ? '目的' : 'Purpose'}</th>
                <th className="py-2 font-medium">{isJa ? '所在地' : 'Region'}</th>
              </tr>
            </thead>
            <tbody>
              {SUBPROCESSORS.map((s) => (
                <tr key={s.name} className="border-b border-ink-100">
                  <td className="py-2 pr-4 text-ink-900">{s.name}</td>
                  <td className="py-2 pr-4">{isJa ? s.purpose[1] : s.purpose[0]}</td>
                  <td className="py-2">{isJa ? s.region[1] : s.region[0]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </LegalSection>
    </LegalLayout>
  );
}
